/* eslint-disable react-hooks/exhaustive-deps */
import { FilterOptionsState, TextField, Tooltip } from "@mui/material";
import { Autocomplete } from '@mui/material';
import React, { FC, useState, useEffect, ReactNode } from "react";
import { IDataRequest, IDataResponse, IHttpClient, STATUS_CODE } from "src/framework/providers/api";

type InputComboBoxProperties = {
    id?: string;
    value?: any;
    provider: IHttpClient;
    path: string;
    label?: string;
    textField: string;
    valueField: string;
    autoComplete?: boolean;
    disabled?: boolean;
    helperText?: ReactNode;
    prefix?: ReactNode;
    renderOptions?: (option: any) => string;
    renderOptionsLabel?: (option: any) => string;
    onChange: (value: any) => void;
    onBlur?: Function;
    onFocus?: Function;
    filterOptions?: (opts: any[], state: FilterOptionsState<any>) => any[]
}

const InputComboBox: FC<InputComboBoxProperties> = ({
    id,
    label,
    value,
    provider,
    path,
    textField,
    disabled = false,
    autoComplete = false,
    valueField,
    renderOptions,
    renderOptionsLabel,
    onChange,
    helperText,
    prefix,
    onBlur,
    onFocus,
    filterOptions
}) => {
    const pathPrevRef = React.useRef("");

    const [options, setOptions] = useState<any[]>([]);
    const [localValue, setLocalValue] = React.useState<any>();
    const [inputValue] = React.useState('');

    const getOptions = async (): Promise<IDataResponse> => {
        const request: IDataRequest = {
            url: path
        }

        return await provider.get(request);
    }

    const loadData = () => {
        if (pathPrevRef.current === path) {
            return;
        }
        pathPrevRef.current = path;

        getOptions()
            .then((response) => {
                if (response.statusCode === STATUS_CODE.OK) {
                    setOptions(response?.data?.rows || response?.data || [])
                    return;
                } else {
                    setOptions([]);
                }
            })
            .catch((error) => { })
            .finally(() => {});
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        loadData();
    }, [path]);

    return (
        <>
            <Tooltip title={localValue && renderOptions ? renderOptions(localValue) : ""} >
                <Autocomplete
                    filterOptions={filterOptions}
                    fullWidth
                    id={id}
                    value={value}
                    disabled={disabled}
                    options={options}
                    autoComplete={autoComplete}
                    getOptionLabel={(option) =>
                        renderOptionsLabel
                        ? renderOptionsLabel(option)
                        : option[textField]
                    }
                    isOptionEqualToValue={(option, value) =>
                        option[valueField] === value[valueField]
                    }
                    onChange={(event, value) => {
                        onChange(value);
                    }}
                    renderOption={(props, option) => (
                        <li {...props}>
                        {renderOptions
                            ? renderOptions(option)
                            : option[textField]}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            helperText={helperText}
                            InputLabelProps={{ shrink: true }}
                            label={label}
                            variant="outlined"
                            size="small"
                            margin="normal"
                            fullWidth
                            value={inputValue}
                        />
                    )}
                    onFocus={(event) => onFocus?.(event)}
                    onBlur={(event) => onBlur?.(event)}
                />
            </Tooltip>
        </>
    );
}

export { InputComboBox }